import { Injectable } from '@angular/core';

import { ConfigParserService } from '../shared/services/config-parser.service';
import { LocalStorageService } from '../shared/services/local-storage.service';
import { ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { ContextService } from '../shared/shared-context/context.service';
import { AppCategory } from './app-gallery-models/category.model';

export interface AppGalleryConfig {
    numberOfBusinessApps: number;
    learnMoreLinks: object;
    resources: AppGalleryResources;
    categoryId: string;
    workspaceBatches: boolean;
    myApplicationsUrl: string;
    ecsAppGalleryUpdatesEnabled: boolean;
    filterAllAppsFirstPartyEnabled: boolean;
}

interface AppGalleryResources {
    allApps: string;
    allTabTitle: string;
    businessAppsTabTitle: string;
    listTitle: string;
    office365Text: string;
    microsoft365Text: string;
    openText: string;
    otherTabTitle: string;
    otherText: string;
    moreFromMicrosoftText: string;
}

@Injectable({
    providedIn: 'root'
})
export class AppGalleryConfigService {
    public readonly config: AppGalleryConfig;

    private categoriesPromise: Promise<AppCategory>;

    private static readonly appCategoryRefreshDataKey = 'AppCategoryRefresh';
    private static appCategoryRefreshLocalStorageDataKey = `${AppGalleryConfigService.appCategoryRefreshDataKey}`;
    private static readonly appCategoriesDataKey = 'AppCategories';
    private static appCategoriesLocalStorageDataKey = `${AppGalleryConfigService.appCategoriesDataKey}`;
    private static readonly appCategoriesOfficeMarketDataKey = 'AppCategoryLanguage';
    private static appCategoriesOfficeMarketLocalStorageDataKey = `${AppGalleryConfigService.appCategoriesOfficeMarketDataKey}`;

    constructor(
        private readonly localStorageService: LocalStorageService,
        private readonly contextService: ContextService,
        private readonly configParserService: ConfigParserService,
        private readonly clientStartupConfig: ClientStartupConfigService
    ) {
        this.config = configParserService.parse<AppGalleryConfig>('appGalleryConfig') || {
            numberOfBusinessApps: 0,
            learnMoreLinks: [],
            resources: null,
            categoryId: 'TipsAndTricks',
            workspaceBatches: false,
            myApplicationsUrl: '',
            ecsAppGalleryUpdatesEnabled: false,
            filterAllAppsFirstPartyEnabled: false
        };

        this.config.categoryId = 'TipsAndTricks';

        AppGalleryConfigService.appCategoryRefreshLocalStorageDataKey += '_' + this.clientStartupConfig.config.tenantId + '_' + this.clientStartupConfig.config.puid;
        AppGalleryConfigService.appCategoriesLocalStorageDataKey += '_' + this.clientStartupConfig.config.tenantId + '_' + this.clientStartupConfig.config.puid;
        AppGalleryConfigService.appCategoriesOfficeMarketLocalStorageDataKey += '_' + this.clientStartupConfig.config.tenantId + '_' + this.clientStartupConfig.config.puid;
    }

    public getCategory(): Promise<AppCategory> {
        if (!this.categoriesPromise) {
            this.categoriesPromise = new Promise<AppCategory>(async resolve => {
                const categoriesLastRefresh = this.localStorageService.get(AppGalleryConfigService.appCategoryRefreshLocalStorageDataKey) || 0;
                const time = new Date().getTime() - categoriesLastRefresh;
                const oneDay = 24 * 60 * 60 * 1000;

                const currentOfficeMarket = this.contextService.sharedContext.officeMarket;
                const previousOfficeMarket = this.localStorageService.get(AppGalleryConfigService.appCategoriesOfficeMarketLocalStorageDataKey) || '';

                if (time <= oneDay && currentOfficeMarket === previousOfficeMarket) {
                    const oldCategory = this.localStorageService.get(AppGalleryConfigService.appCategoriesLocalStorageDataKey) || {} as AppCategory;

                    if (oldCategory.Apps) {
                        return resolve(oldCategory);
                    }
                }

                const category = this.configParserService.parse<AppCategory>('appCategoryData') || {} as AppCategory;

                this.saveToLocalStorage(currentOfficeMarket, category);
                resolve(category);
            });
        }

        return this.categoriesPromise;
    }

    private saveToLocalStorage(currentOfficeMarket: string, categories: AppCategory) {
        this.localStorageService.set(AppGalleryConfigService.appCategoryRefreshLocalStorageDataKey, new Date().getTime());
        this.localStorageService.set(AppGalleryConfigService.appCategoriesOfficeMarketLocalStorageDataKey, currentOfficeMarket);
        this.localStorageService.set(AppGalleryConfigService.appCategoriesLocalStorageDataKey, categories);
    }
}
